import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Mail, Briefcase, Building2, Inbox, Loader2, Check, X, Copy, DollarSign, AlertTriangle, UserX, Eye } from 'lucide-react';
import { inviteService } from '../../services/inviteService';
import { notificationService, NOTIFICATION_TYPES, REFERENCE_TYPES } from '../../services/notificationService';
import { jobService } from '../../services/jobService';
import { userManagementService } from '../../services/userManagementService';
import { useCompany } from '../../contexts/CompanyContext';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../utils/cn';

const NotificationCenter = () => {
  const navigate = useNavigate();
  const { refreshCompanies } = useCompany();
  const { user, profile } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'invites', 'jobs', 'admin'
  const [invites, setInvites] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [orphanedUsers, setOrphanedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [responding, setResponding] = useState(null);
  const [copiedRef, setCopiedRef] = useState(null);
  const dropdownRef = useRef(null);

  // Check if user is superadmin
  const isSuperAdmin = profile?.account_type === 'superadmin';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch data when dropdown opens and mark all as read
  useEffect(() => {
    if (isOpen) {
      fetchAllNotifications();
      // Mark all notifications as read when user opens the panel
      if (user?.id) {
        notificationService.markAllAsRead(user.id).then(() => {
          // Update local state to reflect read status
          setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
        }).catch(err => {
          console.error('Failed to mark notifications as read:', err);
        });
      }
    }
  }, [isOpen, user?.id]);

  // Fetch on mount to show badge
  useEffect(() => {
    fetchAllNotifications();
  }, []);

  // Subscribe to real-time notifications
  useEffect(() => {
    if (!user?.id) return;

    const unsubscribe = notificationService.subscribeToNotifications(
      user.id,
      (newNotification) => {
        setNotifications(prev => [newNotification, ...prev]);
      }
    );

    return () => unsubscribe?.();
  }, [user?.id]);

  const fetchAllNotifications = async () => {
    try {
      setLoading(true);

      // Fetch invites and notifications in parallel
      const fetchPromises = [
        inviteService.getPendingInvites().catch(() => ({ invites: [] })),
        notificationService.getUserNotifications(user.id, { unreadOnly: false, limit: 20 }).catch(() => [])
      ];

      // For superadmins, also fetch orphaned users
      if (isSuperAdmin) {
        fetchPromises.push(
          userManagementService.getOrphanedUsers(1).catch(() => ({ data: [] }))
        );
      }

      const results = await Promise.all(fetchPromises);

      setInvites(results[0].invites || []);
      setNotifications(results[1] || []);

      // Set orphaned users if superadmin
      if (isSuperAdmin && results[2]) {
        setOrphanedUsers(results[2].data || []);
      }
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRespondToInvite = async (inviteId, accept) => {
    try {
      setResponding(inviteId);
      await inviteService.respondToInvite(inviteId, accept);

      // Remove invite from list
      setInvites(prev => prev.filter(inv => inv.id !== inviteId));

      // If accepted, refresh companies list
      if (accept) {
        await refreshCompanies?.();
      }
    } catch (err) {
      console.error('Failed to respond to invite:', err);
    } finally {
      setResponding(null);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationService.markAsRead(notificationId);
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, isRead: true } : n)
      );
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead(user.id);
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const handleNotificationClick = async (notification) => {
    // Handle job-related notifications
    const jobNotificationTypes = [
      NOTIFICATION_TYPES.JOB_ASSIGNED,
      NOTIFICATION_TYPES.JOB_UPDATED,
      NOTIFICATION_TYPES.JOB_NEEDS_REVISION,
      NOTIFICATION_TYPES.JOB_PENDING,
      NOTIFICATION_TYPES.JOB_PENDING_REPORT
    ];

    if (jobNotificationTypes.includes(notification.type)) {
      setIsOpen(false);

      // Mark as read if needed
      if (!notification.isRead) {
        handleMarkAsRead(notification.id);
      }

      try {
        // Fetch instance to check date and route appropriately
        if (notification.referenceType === REFERENCE_TYPES.JOB_INSTANCE && notification.referenceId) {
          const instance = await jobService.getJobInstance(notification.referenceId);

          if (instance) {
            // Determine if user is contractor or corporate based on account type
            const isCorporateUser = profile?.account_type === 'corporate' || profile?.account_type === 'superadmin';

            if (isCorporateUser) {
              // Corporate users go to Job Management page with the instance highlighted
              // The job ID is needed to expand the correct job row
              navigate(`/corporate/job-management?highlight=${instance.id}&jobId=${instance.jobId}`);
            } else {
              // Contractor navigation based on job date
              if (instance.scheduledDate) {
                const jobDate = new Date(instance.scheduledDate);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                jobDate.setHours(0, 0, 0, 0);

                if (jobDate.getTime() === today.getTime()) {
                  navigate(`/contractor/today-jobs?highlight=${instance.id}`);
                } else if (jobDate.getTime() > today.getTime()) {
                  navigate(`/contractor/upcoming-jobs?highlight=${instance.id}`);
                } else {
                  navigate(`/contractor/earnings?highlight=${instance.id}`);
                }
              }
            }
          }
        }
      } catch (err) {
        console.error('Failed to route notification:', err);
      }
    } else {
      console.log('Notification type not handled for deep linking:', notification.type);
    }
  };

  const formatRole = (role) => {
    return role?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffMs / (1000 * 60));

    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case NOTIFICATION_TYPES.JOB_ASSIGNED:
      case NOTIFICATION_TYPES.JOB_UPDATED:
      case NOTIFICATION_TYPES.JOB_CANCELLED:
        return <Briefcase size={18} className="text-primary-orange" />;
      case NOTIFICATION_TYPES.REPORT_SUBMITTED:
      case NOTIFICATION_TYPES.REPORT_APPROVED:
      case NOTIFICATION_TYPES.REPORT_REJECTED:
        return <Check size={18} className="text-accent-green" />;
      case NOTIFICATION_TYPES.PAYMENT_RECORDED:
        return <DollarSign size={18} className="text-green-400" />;
      case NOTIFICATION_TYPES.JOB_PENDING:
      case NOTIFICATION_TYPES.JOB_PENDING_REPORT:
        return <AlertTriangle size={18} className="text-red-400" />;
      default:
        return <Bell size={18} className="text-primary-orange" />;
    }
  };

  // Extract transaction ref from payment notification message
  const extractTransactionRef = (message) => {
    if (!message) return null;
    const match = message.match(/Ref:\s*(\S+)$/);
    return match ? match[1] : null;
  };

  // Copy transaction ref to clipboard
  const handleCopyTransactionRef = async (e, notificationId, transactionRef) => {
    e.stopPropagation(); // Prevent notification click
    try {
      await navigator.clipboard.writeText(transactionRef);
      setCopiedRef(notificationId);
      setTimeout(() => setCopiedRef(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Filter items based on active tab
  const getFilteredItems = () => {
    if (activeTab === 'invites') {
      return { invites, notifications: [], orphans: [] };
    }
    if (activeTab === 'jobs') {
      return {
        invites: [],
        notifications: notifications.filter(n =>
          [NOTIFICATION_TYPES.JOB_ASSIGNED, NOTIFICATION_TYPES.JOB_UPDATED, NOTIFICATION_TYPES.JOB_CANCELLED].includes(n.type)
        ),
        orphans: []
      };
    }
    if (activeTab === 'admin') {
      return { invites: [], notifications: [], orphans: orphanedUsers };
    }
    return { invites, notifications, orphans: isSuperAdmin ? orphanedUsers : [] };
  };

  const { invites: filteredInvites, notifications: filteredNotifications, orphans: filteredOrphans } = getFilteredItems();

  // Handle view orphaned user - navigate to All Users page
  const handleViewOrphanedUser = (userId) => {
    setIsOpen(false);
    navigate(`/super-admin/all-users?highlight=${userId}`);
  };

  // Total unread count for badge (include orphaned users for superadmins)
  const totalUnread = invites.length + notifications.filter(n => !n.isRead).length + (isSuperAdmin ? orphanedUsers.length : 0);
  const hasUnreadNotifications = notifications.some(n => !n.isRead);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon with Badge */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-text-secondary hover:text-text-primary hover:bg-surface rounded-lg transition-colors"
        title="Notifications"
      >
        <Bell size={20} />
        {totalUnread > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-orange-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
            {totalUnread > 9 ? '9+' : totalUnread}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-full right-0 mt-2 w-96 bg-background border border-border rounded-xl shadow-xl z-50 overflow-hidden">
          {/* Header */}
          <div className="p-3 border-b border-border">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <Bell size={16} className="text-primary-orange" />
                <span className="text-text-primary font-medium">Notifications</span>
              </div>
              {hasUnreadNotifications && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="text-xs text-primary-orange hover:text-orange-400 transition-colors"
                >
                  Mark all read
                </button>
              )}
            </div>

            {/* Tabs */}
            <div className="flex gap-1 bg-surface p-1 rounded-lg">
              {[
                { id: 'all', label: 'All' },
                { id: 'invites', label: 'Invites', count: invites.length },
                { id: 'jobs', label: 'Jobs' },
                ...(isSuperAdmin ? [{ id: 'admin', label: 'Admin', count: orphanedUsers.length }] : [])
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                    activeTab === tab.id
                      ? 'bg-primary-orange text-white'
                      : 'text-text-secondary hover:text-text-primary'
                  )}
                >
                  {tab.label}
                  {tab.count > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 bg-black/20 rounded-full text-[10px]">
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-6 text-center">
                <Loader2 size={24} className="animate-spin text-text-secondary mx-auto" />
                <p className="text-text-secondary text-sm mt-2">Loading...</p>
              </div>
            ) : filteredInvites.length === 0 && filteredNotifications.length === 0 && filteredOrphans.length === 0 ? (
              <div className="p-6 text-center">
                <div className="w-12 h-12 bg-surface rounded-full flex items-center justify-center mx-auto mb-3">
                  <Inbox size={24} className="text-text-secondary" />
                </div>
                <p className="text-text-secondary text-sm">No notifications</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {/* Invites */}
                {filteredInvites.map((invite) => (
                  <div key={`invite-${invite.id}`} className="p-4 hover:bg-surface/50 transition-colors">
                    <div className="flex items-start space-x-3">
                      <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Building2 size={18} className="text-primary-orange" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-text-primary font-medium truncate">
                          {invite.company_name}
                        </p>
                        <p className="text-text-secondary text-sm">
                          Invited as <span className="text-primary-orange">{formatRole(invite.role)}</span>
                        </p>
                        {invite.invited_by_name && (
                          <p className="text-text-secondary text-xs mt-1">
                            By {invite.invited_by_name} • {formatDate(invite.created_at)}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center space-x-2 mt-3">
                      <button
                        onClick={() => handleRespondToInvite(invite.id, true)}
                        disabled={responding === invite.id}
                        className="flex-1 px-3 py-2 bg-primary-orange text-white text-sm font-medium rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 flex items-center justify-center"
                      >
                        {responding === invite.id ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <>
                            <Check size={14} className="mr-1" />
                            Accept
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => handleRespondToInvite(invite.id, false)}
                        disabled={responding === invite.id}
                        className="flex-1 px-3 py-2 bg-background text-text-secondary text-sm font-medium rounded-lg hover:bg-surface transition-colors disabled:opacity-50 flex items-center justify-center border border-border"
                      >
                        <X size={14} className="mr-1" />
                        Decline
                      </button>
                    </div>
                  </div>
                ))}

                {/* General Notifications */}
                {filteredNotifications.map((notification) => {
                  const transactionRef = notification.type === NOTIFICATION_TYPES.PAYMENT_RECORDED
                    ? extractTransactionRef(notification.message)
                    : null;

                  return (
                    <div
                      key={`notif-${notification.id}`}
                      className={cn(
                        'p-4 hover:bg-surface/50 transition-colors cursor-pointer',
                        !notification.isRead && 'bg-primary-orange/5'
                      )}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="flex items-start space-x-3">
                        <div className={cn(
                          'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0',
                          notification.type === NOTIFICATION_TYPES.PAYMENT_RECORDED
                            ? 'bg-green-500/20'
                            : !notification.isRead ? 'bg-primary-orange/20' : 'bg-surface'
                        )}>
                          {getNotificationIcon(notification.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <p className={cn(
                              'font-medium truncate',
                              notification.isRead ? 'text-text-secondary' : 'text-text-primary'
                            )}>
                              {notification.title}
                            </p>
                            {!notification.isRead && (
                              <span className="w-2 h-2 bg-primary-orange rounded-full flex-shrink-0 mt-2 ml-2" />
                            )}
                          </div>
                          {notification.message && (
                            <p className="text-text-secondary text-sm line-clamp-2 mt-0.5">
                              {notification.message}
                            </p>
                          )}
                          {/* Copy Transaction Ref button for payment notifications */}
                          {transactionRef && (
                            <button
                              onClick={(e) => handleCopyTransactionRef(e, notification.id, transactionRef)}
                              className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium bg-surface hover:bg-surface-light border border-border rounded-md transition-colors"
                            >
                              {copiedRef === notification.id ? (
                                <>
                                  <Check size={12} className="text-green-400" />
                                  <span className="text-green-400">Copied!</span>
                                </>
                              ) : (
                                <>
                                  <Copy size={12} className="text-text-secondary" />
                                  <span className="text-text-secondary font-mono">{transactionRef}</span>
                                </>
                              )}
                            </button>
                          )}
                          <p className="text-text-tertiary text-xs mt-1">
                            {formatDate(notification.createdAt)}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Orphaned Users (Admin Tab) */}
                {filteredOrphans.map((orphan) => (
                  <div key={`orphan-${orphan.user_id}`} className="p-4 hover:bg-surface/50 transition-colors bg-red-500/5">
                    <div className="flex items-start space-x-3">
                      <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <UserX size={18} className="text-red-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <AlertTriangle size={14} className="text-yellow-500 flex-shrink-0" />
                          <p className="text-text-primary font-medium truncate">
                            Orphaned User
                          </p>
                        </div>
                        <p className="text-text-secondary text-sm mt-0.5 truncate">
                          {orphan.email}
                        </p>
                        <p className="text-text-tertiary text-xs mt-0.5">
                          {orphan.full_name || 'No name'} • {orphan.account_type}
                        </p>
                        <p className="text-text-tertiary text-xs">
                          Created {Math.round(orphan.age_hours)}h ago
                          {orphan.safe_to_delete && (
                            <span className="ml-2 text-green-400">• Safe to delete</span>
                          )}
                        </p>

                        {/* View Button */}
                        <button
                          onClick={() => handleViewOrphanedUser(orphan.user_id)}
                          className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-primary-orange text-white rounded-md hover:bg-orange-600 transition-colors"
                        >
                          <Eye size={12} />
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
